import * as React from "react";
import { format } from "date-fns";
import { Button } from "./ui/button";
import { Loader } from "lucide-react";
const Dialog = React.lazy(() =>
  import("./ui/dialog").then((module) => ({ default: module.Dialog }))
);
const DialogContent = React.lazy(() =>
  import("./ui/dialog").then((module) => ({ default: module.DialogContent }))
);
const DialogHeader = React.lazy(() =>
  import("./ui/dialog").then((module) => ({ default: module.DialogHeader }))
);
const DialogFooter = React.lazy(() =>
  import("./ui/dialog").then((module) => ({ default: module.DialogFooter }))
);
const DialogTitle = React.lazy(() =>
  import("./ui/dialog").then((module) => ({ default: module.DialogTitle }))
);
const DialogDescription = React.lazy(() =>
  import("./ui/dialog").then((module) => ({ default: module.DialogDescription }))
);
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Pencil, Trash2 } from "lucide-react";
import { Chat } from "../../types/research.types";
import { useUpdateChat } from "../../hooks/useUpdateChat";
import { useDeleteChat } from "../../hooks/useDeleteChat";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

interface ResearchChatListProps {
  chats: Chat[];
  searchQuery: string;
  onChatClick: (chatId: string) => void;
}

const ChatItem: React.FC<{
  chat: Chat;
  onChatClick: (chatId: string) => void;
  onEditClick: (chatId: string, title: string) => void;
  onDeleteClick: (chatId: string) => void;
  isDisabled: boolean;
}> = ({ chat, onChatClick, onEditClick, onDeleteClick, isDisabled }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex items-center justify-between px-4 py-3.5 bg-primary-100 rounded-lg  transition-all cursor-pointer "
          onClick={() => onChatClick(chat.chat_id)}
        >
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <p className="text-sm font-medium text-neutral-400 truncate text-left">
              {chat.chat_name}
            </p>
          </div>
          <div className="flex items-center gap-3 ml-2">
            <p className="text-xs text-neutral-500">
              {format(new Date(chat.creation_date), "do MMM, yyyy")}
            </p>
            <Button
              variant="ghost"
              className="w-auto h-auto p-0"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(chat.chat_id, chat.chat_name);
              }}
              disabled={isDisabled}
            >
              <Pencil className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              className="w-auto h-auto p-0"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(chat.chat_id);
              }}
              disabled={isDisabled}
            >
              <Trash2 className="size-3.5 text-danger-200" />
            </Button>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">
        <p>{chat.chat_name}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const EditChatDialog: React.FC<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onTitleChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isPending: boolean;
}> = ({ isOpen, onOpenChange, title, onTitleChange, onSave, onCancel, isPending }) => (
  <React.Suspense fallback={<Loader className="animate-spin" />}>
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-4">
        <DialogHeader className="sm:text-center">
          <DialogTitle className="text-base font-medium text-center">Edit Chat Title</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="chatTitle" className="font-medium text-sm leading-5 text-neutral-400">
              Chat Title
            </Label>
            <Input
              id="chatTitle"
              value={title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="Enter chat title"
              className="placeholder:text-neutral-500 text-sm py-3 px-4 h-auto"
            />
          </div>
        </div>
        <DialogFooter className="flex-row gap-2 sm:justify-center">
          <Button variant="secondary" onClick={onCancel} disabled={isPending} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={onSave}
            disabled={!title.trim() || isPending}
            className="flex-1"
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </React.Suspense>
);

const DeleteChatDialog: React.FC<{
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}> = ({ isOpen, onOpenChange, onConfirm, onCancel, isPending }) => (
  <React.Suspense fallback={<Loader className="animate-spin" />}>
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-4">
        <DialogHeader className="sm:text-center">
          <DialogTitle className="text-base font-medium text-center">Delete Chat</DialogTitle>
          <DialogDescription className="text-center">
            Are you sure you want to delete this chat? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-row gap-2 sm:justify-center">
          <Button variant="secondary" onClick={onCancel} disabled={isPending} className="flex-1">
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending} className="flex-1">
            {isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </React.Suspense>
);

interface EditState {
  chatId: string | null;
  title: string;
  isOpen: boolean;
}

interface DeleteState {
  chatId: string | null;
  isOpen: boolean;
}

function useEditDialog(updateMutation: ReturnType<typeof useUpdateChat>) {
  const [state, setState] = React.useState<EditState>({ chatId: null, title: "", isOpen: false });

  const open = (chatId: string, currentTitle: string) => {
    setState({ chatId, title: currentTitle, isOpen: true });
  };

  const save = async () => {
    if (state.chatId != null && state.title.trim().length > 0) {
      await updateMutation.mutateAsync({ chat_id: state.chatId, new_name: state.title.trim() });
      setState({ chatId: null, title: "", isOpen: false });
    }
  };

  const cancel = () => setState({ chatId: null, title: "", isOpen: false });

  const setTitle = (title: string) => setState((prev) => ({ ...prev, title }));
  const setIsOpen = (isOpen: boolean) => setState((prev) => ({ ...prev, isOpen }));

  return { state, open, save, cancel, setTitle, setIsOpen };
}

function useDeleteDialog(deleteMutation: ReturnType<typeof useDeleteChat>) {
  const [state, setState] = React.useState<DeleteState>({ chatId: null, isOpen: false });

  const open = (chatId: string) => setState({ chatId, isOpen: true });

  const confirm = async () => {
    if (state.chatId != null) {
      await deleteMutation.mutateAsync(state.chatId);
      setState({ chatId: null, isOpen: false });
    }
  };

  const cancel = () => setState({ chatId: null, isOpen: false });
  const setIsOpen = (isOpen: boolean) => setState((prev) => ({ ...prev, isOpen }));

  return { state, open, confirm, cancel, setIsOpen };
}

const ChatListContent: React.FC<{
  chats: Chat[];
  searchQuery: string;
  onChatClick: (chatId: string) => void;
  onEditClick: (chatId: string, title: string) => void;
  onDeleteClick: (chatId: string) => void;
  isDisabled: boolean;
}> = ({ chats, searchQuery, onChatClick, onEditClick, onDeleteClick, isDisabled }) => (
  <div className="flex-1 overflow-auto flex flex-col gap-2">
    {chats.length === 0 ? (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <p className="text-sm text-neutral-400">
          {searchQuery.length > 0
            ? "No chats found matching your search."
            : "No chats available yet."}
        </p>
      </div>
    ) : (
      chats.map((chat) => (
        <ChatItem
          key={chat.chat_id}
          chat={chat}
          onChatClick={onChatClick}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          isDisabled={isDisabled}
        />
      ))
    )}
  </div>
);

const ResearchChatList: React.FC<ResearchChatListProps> = ({ chats, searchQuery, onChatClick }) => {
  const updateChatMutation = useUpdateChat();
  const deleteChatMutation = useDeleteChat();
  const editDialog = useEditDialog(updateChatMutation);
  const deleteDialog = useDeleteDialog(deleteChatMutation);
  const isDisabled = updateChatMutation.isPending || deleteChatMutation.isPending;

  return (
    <>
      <ChatListContent
        chats={chats}
        searchQuery={searchQuery}
        onChatClick={onChatClick}
        onEditClick={editDialog.open}
        onDeleteClick={deleteDialog.open}
        isDisabled={isDisabled}
      />
      <EditChatDialog
        isOpen={editDialog.state.isOpen}
        onOpenChange={editDialog.setIsOpen}
        title={editDialog.state.title}
        onTitleChange={editDialog.setTitle}
        onSave={editDialog.save}
        onCancel={editDialog.cancel}
        isPending={updateChatMutation.isPending}
      />
      <DeleteChatDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={deleteDialog.setIsOpen}
        onConfirm={deleteDialog.confirm}
        onCancel={deleteDialog.cancel}
        isPending={deleteChatMutation.isPending}
      />
    </>
  );
};

export default ResearchChatList;
